import { Paginated } from 'types/paginated';
import {
  User,
  IStatistics,
  UserTrackFieldHistory,
  IUserLog,
} from '../types/typeUser';
import { $api } from './interceptor';
import { UserReferrals } from 'views/types/referrals';
import {
  IUpdateUserBalanceResponse,
  IUserCurrencyBalance,
  TBalanceType,
} from 'views/types/balances';

interface Params {
  page?: number;
  pageSize?: number;
  searchValue?: string;
  sortBy?: string;
  sortOrder?: string;
  filter?: Record<string, string>;
}

const usersApi = () => {
  const getUsersStatistic = async () => {
    const { data } = await $api.get<IStatistics>('api/admin/statistic');
    return data;
  };

  const getUsersList = async () => {
    const { data } = await $api.get<Paginated<User[]>>('api/users/list');
    return data.data;
  };

  const getPaginatedUsersList = async ({
    page = 0,
    pageSize = 10,
    searchValue = '',
    sortBy,
    sortOrder,
    filter = {},
  }: Params): Promise<Paginated<User>> => {
    const params: Record<string, string> = {
      page: String(page + 1),
      limit: String(pageSize),
      search: searchValue,
    };
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;

    const searchParams = new URLSearchParams(params);

    Object.entries(filter).forEach(([key, val]) => {
      if (val != null) {
        searchParams.append(`filter.${key}`, val);
      }
    });

    const url = `api/users/list?${searchParams.toString()}`;

    const { data } = await $api.get<Paginated<User>>(url);
    return data;
  };

  const getPaginatedLogsList = async ({
    page = 0,
    pageSize = 10,
    userId,
  }: {
    page?: number;
    pageSize?: number;
    userId?: string;
  }): Promise<Paginated<IUserLog>> => {
    // eslint-disable-next-line
    const params: any = { page: page + 1, limit: pageSize };

    if (userId) {
      params['filter.user_entity_id'] = `$eq:${userId}`;
    }

    const { data } = await $api.get<Paginated<IUserLog>>('api/logs', {
      params,
    });
    return data;
  };

  const getUser = async (userID: string) => {
    try {
      const response = await $api.get<Paginated<User>>('api/users/list', {
        params: { page: 1, limit: 10, search: userID },
      });

      const user = response.data.data?.at(0);

      if (!user) {
        return;
      }
      return user;
    } catch (error) {
      console.error(error);
    }
  };

  const getUserReferrals = async (userId: string) => {
    const { data } = await $api.get<UserReferrals>(
      `api/users/${userId}/referrals`
    );
    return data;
  };

  const patchUser = async (userID: number, user: User) => {
    await $api.patch(`/users/${userID}`, { ...user });
  };

  const downloadCSV = async (filterCacheKey: string) => {
    const { data } = await $api.get('api/users/csv', {
      params: { idsCacheKey: filterCacheKey },
      responseType: 'blob',
    });

    const virtualLink = window.document.createElement('a');
    virtualLink.href = window.URL.createObjectURL(data);
    virtualLink.download = `users-${new Date().toISOString()}.csv`;
    virtualLink.click();
    virtualLink.remove();
  };

  const downloadPredictionsAsCSV = async (take: number) => {
    const { data } = await $api.get('api/predictions/csv', {
      responseType: 'blob',
      params: { take },
    });

    const virtualLink = window.document.createElement('a');
    virtualLink.href = window.URL.createObjectURL(data);
    virtualLink.download = `predictions-${new Date().toISOString()}.csv`;
    virtualLink.click();
    virtualLink.remove();
  };

  const getUserChangesHistory = async (id: string) => {
    try {
      const url = `api/users/${id}/change-log-list`;
      const { data } = await $api.get<Paginated<UserTrackFieldHistory>>(url);
      const userHistory = data.data;
      return userHistory;
    } catch (error) {
      console.error(error);
    }
  };

  const toggleBlockUser = async (currentValue: boolean, userId: string) => {
    await $api.post(`/api/users/${currentValue ? 'block' : 'unblock'}`, {
      userId,
    });
  };

  const disableUser2Fa = async (userId: string) => {
    await $api.post(`/api/users/disable-2fa/${userId}`);
  };

  const getUserBalances = async (userId: string) => {
    const { data } = await $api.get<{ balances: IUserCurrencyBalance[] }>(
      `api/users/${userId}/balances`
    );
    return data.balances;
  };

  const updateUserBalance = async (
    userId: string,
    currency: string,
    balanceType: TBalanceType,
    newBalance: string
  ) => {
    const { data } = await $api.patch<IUpdateUserBalanceResponse>(
      `api/users/${userId}/balance`,
      { currency, balanceType, newBalance }
    );
    return data;
  };

  const exportLogsToCsv = async (userId: string) => {
    try {
      const response = await $api.post(
        'api/logs/export-csv',
        { userId },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_${userId}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export Logs CSV failed', err);
    }
  };

  return {
    getUsersStatistic,
    getUsersList,
    getUser,
    getUserReferrals,
    patchUser,
    getPaginatedUsersList,
    downloadCSV,
    downloadPredictionsAsCSV,
    getUserChangesHistory,
    getPaginatedLogsList,
    toggleBlockUser,
    disableUser2Fa,
    getUserBalances,
    updateUserBalance,
    exportLogsToCsv,
  };
};

export const usersService = usersApi();
